package link.erpango.quip.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Card
import androidx.compose.material.CircularProgressIndicator
import androidx.compose.material.Divider
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Scaffold
import androidx.compose.material.Surface
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Build
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.filled.Share
import androidx.compose.runtime.Composable
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.RectangleShape
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import compose.icons.FontAwesomeIcons
import compose.icons.fontawesomeicons.Solid
import compose.icons.fontawesomeicons.solid.Toolbox
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import androidx.compose.runtime.remember
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.runtime.derivedStateOf
import compose.icons.fontawesomeicons.solid.Calendar
import compose.icons.fontawesomeicons.solid.File
import compose.icons.fontawesomeicons.solid.Fire
import link.erpango.quip.domain.entities.DCompatibleVehicle
import link.erpango.quip.domain.entities.DMaintenance
import link.erpango.quip.domain.entities.DMySparePart
import link.erpango.quip.domain.entities.DVehicle
import link.erpango.quip.domain.entities.DVehicleBrand
import link.erpango.quip.features.myspareparts.MySparePartsUiState
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.ui.components.QuipCleanInfoRow
import link.erpango.quip.ui.components.QuipGradientCard
import link.erpango.quip.ui.components.QuipPrimaryButton
import link.erpango.quip.ui.components.QuipQuickActionButton
import link.erpango.quip.ui.components.QuipSectionHeader
import link.erpango.quip.ui.components.QuipTextBadge
import link.erpango.quip.ui.components.QuipTextButton
import link.erpango.quip.ui.components.QuipTransparentHeader
import link.erpango.quip.utils.CalendarUtils
import link.erpango.quip.utils.DatetimeUtils
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.*

/**
 * Pantalla de detalle de vehículo rediseñada (Light Visual Identity)
 */
@Composable
fun VehicleDetailScreen(
    vehicle: DVehicle,
    maintenances: List<DMaintenance>,
    sparePartsState: MySparePartsUiState,
    vehicleBrands: List<DVehicleBrand>,
    onMaintenanceClick: (DMaintenance) -> Unit,
    onAddMaintenanceClick: () -> Unit,
    onEditVehicleClick: () -> Unit,
    onShareVehicleClick: () -> Unit,
    onSignAllClick: () -> Unit,
    onDocumentsClick: () -> Unit,
    onTransferVehicleClick: () -> Unit = {},
    onAddSparePartClick: () -> Unit,
    onRetrySpareParts: () -> Unit,
    onSparePartClick: (DMySparePart) -> Unit,
    onBackClick: () -> Unit
) {
    val haptic = LocalHapticFeedback.current
    Scaffold(
        backgroundColor = QuipColors.colorBackground
    ) { paddingValues ->
        var isGroupedByYear by remember { mutableStateOf(false) }

        val totalMaintenanceCost by remember(maintenances) {
             derivedStateOf { maintenances.sumOf { it.cost } }
        }

        val groupedMaintenances by remember(maintenances) {
            derivedStateOf {
                maintenances.groupBy { 
                    Instant.fromEpochMilliseconds(it.date)
                        .toLocalDateTime(TimeZone.UTC).year
                }.toList()
                 .sortedByDescending { it.first }
                 .toMap()
            }
        }

        Column(modifier = Modifier.fillMaxSize().padding(paddingValues)) {
            
            // 1. Transparent Header + Gradient Card
            QuipTransparentHeader {
                // Back Button Row
                Row(
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    IconButton(onClick = onBackClick) {
                        Icon(
                            Icons.Default.ArrowBack,
                            contentDescription = "Back",
                            tint = QuipColors.colorText
                        )
                    }
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = stringResource(Res.string.vehicle_details),
                        style = MaterialTheme.typography.h6,
                        color = QuipColors.colorText
                    )
                }
                
                Spacer(modifier = Modifier.height(16.dp))

                // Vehicle Info (Apple-style Gradient Card)
                QuipGradientCard(
                    vehicleColor = vehicle.color
                ) {
                    Column {
                        Text(
                            text = vehicle.plate,
                            style = MaterialTheme.typography.h4,
                            fontWeight = FontWeight.Bold,
                            color = Color.Black // Strong contrast
                        )
                        Text(
                            text = "${vehicle.brand} ${vehicle.model} • ${vehicle.year}",
                            style = MaterialTheme.typography.body1,
                            color = QuipColors.colorText
                        )
                        if (vehicle.vin.isNotBlank()) {
                            Text(
                                text = vehicle.vin,
                                style = MaterialTheme.typography.caption,
                                color = QuipColors.colorTextSecondary,
                                modifier = Modifier.padding(top = 4.dp)
                            )
                        }
                    }
                }
            }

            // 2. Scrollable Content
            LazyColumn(
                modifier = Modifier
                    .fillMaxSize()
                    .weight(1f),
                contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp)
            ) {
                
                // Quick Actions (wraps to multiple rows on narrow screens)
                item {
                    FlowRow(
                        modifier = Modifier.fillMaxWidth().padding(top = 8.dp, bottom = 8.dp),
                        maxItemsInEachRow = Int.MAX_VALUE
                    ) {
                        QuipQuickActionButton(
                            icon = Icons.Default.Edit,
                            label = stringResource(Res.string.edit_vehicle),
                            onClick = onEditVehicleClick,
                            modifier = Modifier.padding(end = 4.dp, bottom = 4.dp)
                        )
                        QuipQuickActionButton(
                            icon = Icons.Default.Share,
                            label = stringResource(Res.string.share),
                            onClick = onShareVehicleClick,
                            modifier = Modifier.padding(end = 4.dp, bottom = 4.dp)
                        )
                        QuipQuickActionButton(
                            icon = Icons.Default.Check,
                            label = stringResource(Res.string.sign_all),
                            onClick = onSignAllClick,
                            modifier = Modifier.padding(end = 4.dp, bottom = 4.dp)
                        )
                        QuipQuickActionButton(
                            icon = FontAwesomeIcons.Solid.File,
                            label = "Docs",
                            onClick = onDocumentsClick,
                            modifier = Modifier.padding(end = 4.dp, bottom = 4.dp)
                        )
                        QuipQuickActionButton(
                            icon = Icons.Default.Send,
                            label = "Transferir",
                            onClick = onTransferVehicleClick,
                            modifier = Modifier.padding(end = 4.dp, bottom = 4.dp)
                        )
                        QuipQuickActionButton(
                            icon = Icons.Default.Add,
                            label = stringResource(Res.string.add_maintenance),
                            onClick = onAddMaintenanceClick,
                            color = QuipColors.colorPrimary, // Highlight add action
                            modifier = Modifier.padding(end = 4.dp, bottom = 4.dp)
                        )
                    }
                    Spacer(modifier = Modifier.height(24.dp))
                }

                // Important Dates Section
                if (vehicle.nextRevisionDate != null || vehicle.nextItvDate != null || vehicle.firstRegistrationDate != null) {
                    item {
                        Column {
                            Text(
                                text = stringResource(Res.string.important_dates),
                                style = MaterialTheme.typography.subtitle1,
                                fontWeight = FontWeight.Bold,
                                color = QuipColors.colorText
                            )
                            Spacer(modifier = Modifier.height(12.dp))

                            Card(
                                elevation = 0.dp,
                                backgroundColor = Color.White,
                                shape = RoundedCornerShape(12.dp),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Column(modifier = Modifier.padding(16.dp)) {
                                    if (vehicle.firstRegistrationDate != null) {
                                        QuipCleanInfoRow(
                                            label = stringResource(Res.string.first_registration_date),
                                            value = DatetimeUtils.dateToSpecificFormat(
                                                DatetimeUtils.getDateFromMilis(vehicle.firstRegistrationDate.toString()),
                                                format = link.erpango.quip.QuipConstants.DateFormat.noUtcPatternSpanishWithoutTime
                                            ),
                                            icon = FontAwesomeIcons.Solid.Calendar
                                        )
                                        Divider(
                                            modifier = Modifier.padding(vertical = 8.dp),
                                            color = QuipColors.colorBackground
                                        )
                                    }
                                    if (vehicle.nextRevisionDate != null) {
                                        QuipCleanInfoRow(
                                            label = stringResource(Res.string.next_revision_date),
                                            value = DatetimeUtils.dateToSpecificFormat(
                                                DatetimeUtils.getDateFromMilis(vehicle.nextRevisionDate.toString()),
                                                format = link.erpango.quip.QuipConstants.DateFormat.noUtcPatternSpanishWithoutTime
                                            ),
                                            icon = FontAwesomeIcons.Solid.Calendar,
                                            trailingIcon = Icons.Default.DateRange,
                                            onClick = {
                                                haptic.performHapticFeedback(HapticFeedbackType.LongPress)
                                                CalendarUtils.addEvent(
                                                    title = "Revisión ${vehicle.brand} ${vehicle.model} (${vehicle.plate})",
                                                    description = "Próxima revisión del vehículo ${vehicle.brand} ${vehicle.model} con matrícula ${vehicle.plate}.",
                                                    startMs = vehicle.nextRevisionDate
                                                )
                                            }
                                        )
                                    }
                                    if (vehicle.nextRevisionDate != null && vehicle.nextItvDate != null) {
                                        Divider(
                                            modifier = Modifier.padding(vertical = 8.dp),
                                            color = QuipColors.colorBackground
                                        )
                                    }
                                    if (vehicle.nextItvDate != null) {
                                        QuipCleanInfoRow(
                                            label = stringResource(Res.string.next_itv_date),
                                            value = DatetimeUtils.dateToSpecificFormat(
                                                DatetimeUtils.getDateFromMilis(vehicle.nextItvDate.toString()),
                                                format = link.erpango.quip.QuipConstants.DateFormat.noUtcPatternSpanishWithoutTime
                                            ),
                                            icon = FontAwesomeIcons.Solid.Calendar,
                                            trailingIcon = Icons.Default.DateRange,
                                            onClick = {
                                                haptic.performHapticFeedback(HapticFeedbackType.LongPress)
                                                CalendarUtils.addEvent(
                                                    title = "ITV ${vehicle.brand} ${vehicle.model} (${vehicle.plate})",
                                                    description = "Próxima ITV del vehículo ${vehicle.brand} ${vehicle.model} con matrícula ${vehicle.plate}.",
                                                    startMs = vehicle.nextItvDate
                                                )
                                            }
                                        )
                                    }
                                }
                            }
                        }
                        Spacer(modifier = Modifier.height(24.dp))
                    }
                }

                // Vehicle Specs Section
                item {
                    Column {
                        Text(
                            text = stringResource(Res.string.specifications),
                            style = MaterialTheme.typography.subtitle1,
                            fontWeight = FontWeight.Bold,
                            color = QuipColors.colorText
                        )
                        Spacer(modifier = Modifier.height(12.dp))
                        
                        Card(
                            elevation = 0.dp,
                            backgroundColor = Color.White,
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                QuipCleanInfoRow(
                                    label = stringResource(Res.string.cylinders),
                                    value = vehicle.cylinderCount.toString(),
                                    icon = FontAwesomeIcons.Solid.Fire
                                )
                                QuipCleanInfoRow(
                                    label = stringResource(Res.string.engine_oil),
                                    value = vehicle.engineOil
                                )
                                Divider(modifier = Modifier.padding(vertical = 8.dp), color = QuipColors.colorBackground)
                                QuipCleanInfoRow(
                                    label = stringResource(Res.string.front_tires).replace(" ", "\n"),
                                    value = "${vehicle.frontTireSize} (${vehicle.frontTirePressureBelow160} bar)"
                                )
                                QuipCleanInfoRow(
                                    label = stringResource(Res.string.rear_tires).replace(" ", "\n"),
                                    value = "${vehicle.rearTireSize} (${vehicle.rearTirePressureBelow160} bar)"
                                )
                            }
                        }
                    }
                    Spacer(modifier = Modifier.height(24.dp))
                }

                // Maintenances Section
                item {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                           // Header manual para evitar fillMaxWidth del componente QuipSectionHeader
                            Icon(
                                imageVector = FontAwesomeIcons.Solid.Toolbox,
                                contentDescription = null,
                                tint = QuipColors.colorPrimary,
                                modifier = Modifier.size(24.dp)
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                text = stringResource(Res.string.maintenances),
                                style = MaterialTheme.typography.h6,
                                fontWeight = FontWeight.Bold,
                                color = QuipColors.colorPrimary
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                text = stringResource(Res.string.total_cost).replace("%s", totalMaintenanceCost.toString()),
                                style = MaterialTheme.typography.body2,
                                color = QuipColors.colorTextSecondary
                            )
                        }

                        IconButton(onClick = { isGroupedByYear = !isGroupedByYear }) {
                            Icon(
                                imageVector = Icons.Default.DateRange,
                                contentDescription = stringResource(Res.string.group_by_year),
                                tint = if (isGroupedByYear) QuipColors.colorPrimary else QuipColors.colorTextSecondary
                            )
                        }
                    }
                    Spacer(modifier = Modifier.height(12.dp))
                }

                if (isGroupedByYear) {
                    groupedMaintenances.forEach { (year, yearMaintenances) ->
                        val yearTotal = yearMaintenances.sumOf { it.cost }
                        item {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(vertical = 8.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(
                                    text = year.toString(),
                                    style = MaterialTheme.typography.h6,
                                    fontWeight = FontWeight.Bold,
                                    color = Color.Black
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Text(
                                    text = stringResource(Res.string.total_cost).replace("%s", yearTotal.toString()),
                                    style = MaterialTheme.typography.body2,
                                    color = QuipColors.colorTextSecondary
                                )
                            }
                        }
                        itemsIndexed(yearMaintenances) { index, maintenance ->
                            val shape = when {
                                yearMaintenances.size == 1 -> RoundedCornerShape(12.dp)
                                index == 0 -> RoundedCornerShape(topStart = 12.dp, topEnd = 12.dp)
                                index == yearMaintenances.size - 1 -> RoundedCornerShape(
                                    bottomStart = 12.dp,
                                    bottomEnd = 12.dp
                                )

                                else -> RectangleShape
                            }

                            LightMaintenanceItem(
                                maintenance = maintenance,
                                onClick = { onMaintenanceClick(maintenance) },
                                shape = shape,
                                showDivider = index < yearMaintenances.size - 1
                            )
                        }
                        item {
                            Spacer(modifier = Modifier.height(16.dp))
                        }
                    }
                } else {
                    itemsIndexed(maintenances) { index, maintenance ->
                        val shape = when {
                            maintenances.size == 1 -> RoundedCornerShape(12.dp)
                            index == 0 -> RoundedCornerShape(topStart = 12.dp, topEnd = 12.dp)
                            index == maintenances.size - 1 -> RoundedCornerShape(
                                bottomStart = 12.dp,
                                bottomEnd = 12.dp
                            )

                            else -> RectangleShape
                        }

                        LightMaintenanceItem(
                            maintenance = maintenance,
                            onClick = { onMaintenanceClick(maintenance) },
                            shape = shape,
                            showDivider = index < maintenances.size - 1
                        )
                    }
                }

                // Spare Parts Section ("Repuestos" enlazados a este vehículo)
                item {
                    VehicleSparePartsSection(
                        vehicle = vehicle,
                        state = sparePartsState,
                        vehicleBrands = vehicleBrands,
                        onAddClick = onAddSparePartClick,
                        onRetry = onRetrySpareParts,
                        onPartClick = onSparePartClick
                    )
                }

                item {
                    Spacer(modifier = Modifier.height(24.dp))
                }

                item {
                    Spacer(modifier = Modifier.height(40.dp))
                }
            }
        }
    }
}

/**
 * Nueva card de mantenimiento más ligera
 */
@Composable
fun LightMaintenanceItem(
    maintenance: DMaintenance,
    onClick: () -> Unit,
    shape: Shape = RoundedCornerShape(12.dp),
    showDivider: Boolean = false
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        elevation = 0.dp, // Flat design
        shape = shape,
        backgroundColor = Color.White
    ) {
        Column {
            Row(
                modifier = Modifier.padding(16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Icon Badge
                Surface(
                    modifier = Modifier.size(48.dp),
                    shape = CircleShape,
                    color = QuipColors.colorSecondary.copy(alpha = 0.3f)
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(
                            imageVector = Icons.Default.Build,
                            contentDescription = null,
                            tint = QuipColors.colorPrimary,
                            modifier = Modifier.size(20.dp)
                        )
                    }
                }
                
                Spacer(modifier = Modifier.width(16.dp))
                
                // Content
                Column(modifier = Modifier.weight(1f)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            text = maintenance.description,
                            style = MaterialTheme.typography.body1,
                            fontWeight = FontWeight.SemiBold,
                            color = QuipColors.colorText
                        )
                        if (maintenance.isSigned) {
                            Spacer(modifier = Modifier.width(6.dp))
                            Icon(
                                imageVector = Icons.Default.Check,
                                contentDescription = "Signed",
                                tint = QuipColors.colorSuccess,
                                modifier = Modifier.size(14.dp)
                            )
                        }
                    }
                    Spacer(modifier = Modifier.height(4.dp))
                    Row(verticalAlignment = Alignment.CenterVertically) {
                         Text(
                            text = DatetimeUtils.formatDateTime(maintenance.date).substringBefore(" "),
                            style = MaterialTheme.typography.caption,
                            color = QuipColors.colorTextSecondary
                        )
                        Text(
                             text = " • ${maintenance.kilometers} km",
                             style = MaterialTheme.typography.caption,
                             color = QuipColors.colorTextSecondary
                        )
                    }
                }
                
                // Cost
                Text(
                    text = "${maintenance.cost}€",
                    style = MaterialTheme.typography.h6,
                    fontWeight = FontWeight.Bold,
                    color = QuipColors.colorText
                )
            }
            
            if (showDivider) {
                Divider(
                    modifier = Modifier.padding(horizontal = 16.dp),
                    color = QuipColors.colorBackground
                )
            }
        }
    }
}

/**
 * Función legacy mantenida para compatibilidad
 */
@Composable
fun MaintenanceItem(maintenance: DMaintenance, onClick: () -> Unit) {
    LightMaintenanceItem(maintenance, onClick)
}

// ============================================================================
// SECCIÓN "REPUESTOS" (repuestos personales enlazados a este vehículo)
// ============================================================================

/**
 * Sección "Repuestos" de [VehicleDetailScreen]: repuestos personales enlazados al vehículo.
 * Muestra las cards de la lista global, botón de alta rápida y chips con los modelos
 * compatibles que coinciden con la marca/modelo del vehículo.
 */
@Composable
fun VehicleSparePartsSection(
    vehicle: DVehicle,
    state: MySparePartsUiState,
    vehicleBrands: List<DVehicleBrand>,
    onAddClick: () -> Unit,
    onRetry: () -> Unit,
    onPartClick: (DMySparePart) -> Unit
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Header manual para evitar fillMaxWidth del componente QuipSectionHeader (igual que Maintenances)
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    imageVector = Icons.Default.Build,
                    contentDescription = null,
                    tint = QuipColors.colorPrimary,
                    modifier = Modifier.size(24.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = stringResource(Res.string.vehicle_detail_spare_parts),
                    style = MaterialTheme.typography.h6,
                    fontWeight = FontWeight.Bold,
                    color = QuipColors.colorPrimary
                )
            }

            IconButton(onClick = onAddClick) {
                Icon(
                    imageVector = Icons.Default.Add,
                    contentDescription = stringResource(Res.string.my_spare_part_edit_title_create),
                    tint = QuipColors.colorPrimary
                )
            }
        }
        Spacer(modifier = Modifier.height(12.dp))

        when {
            state.isLoading && state.parts.isEmpty() -> Box(
                modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(color = QuipColors.colorPrimary, modifier = Modifier.size(24.dp))
            }

            state.error != null && state.parts.isEmpty() -> Column(
                modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = state.error.ifBlank { stringResource(Res.string.my_spare_parts_error_generic) },
                    style = MaterialTheme.typography.body2,
                    color = QuipColors.colorTextSecondary
                )
                Spacer(modifier = Modifier.height(8.dp))
                QuipTextButton(text = stringResource(Res.string.retry), onClick = onRetry)
            }

            state.parts.isEmpty() -> VehicleSparePartsEmptyContent(onAddClick = onAddClick)

            else -> Column(modifier = Modifier.fillMaxWidth()) {
                state.parts.forEach { part ->
                    MySparePartCard(
                        part = part,
                        vehicles = listOf(vehicle),
                        onClick = { onPartClick(part) },
                        modifier = Modifier.padding(vertical = 6.dp)
                    )
                    val matchedLabels = compatibleVehicleLabelsFor(part, vehicle, vehicleBrands)
                    if (matchedLabels.isNotEmpty()) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .horizontalScroll(rememberScrollState())
                                .padding(horizontal = 16.dp),
                            horizontalArrangement = Arrangement.spacedBy(6.dp)
                        ) {
                            matchedLabels.forEach { label ->
                                QuipTextBadge(
                                    text = label,
                                    backgroundColor = QuipColors.colorSecondary.copy(alpha = 0.4f),
                                    textColor = QuipColors.colorText
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun VehicleSparePartsEmptyContent(onAddClick: () -> Unit) {
    Column(
        modifier = Modifier.fillMaxWidth().padding(vertical = 24.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Icon(
            imageVector = Icons.Default.Build,
            contentDescription = null,
            tint = QuipColors.colorPrimary.copy(alpha = 0.4f),
            modifier = Modifier.size(48.dp)
        )
        Spacer(modifier = Modifier.height(16.dp))
        Text(
            text = stringResource(Res.string.my_spare_parts_vehicle_empty_message),
            style = MaterialTheme.typography.body1,
            color = QuipColors.colorTextSecondary
        )
        Spacer(modifier = Modifier.height(24.dp))
        QuipPrimaryButton(
            text = stringResource(Res.string.my_spare_parts_add_first),
            onClick = onAddClick,
            icon = Icons.Default.Add
        )
    }
}

/**
 * Etiquetas ("Marca Modelo" o "Marca") de los vehículos compatibles en [part] que coinciden
 * con [vehicle]. Una entrada solo de marca coincide con cualquier modelo de esa marca.
 * La comparación es insensible a mayúsculas/minúsculas; las etiquetas se deduplican.
 */
internal fun compatibleVehicleLabelsFor(
    part: DMySparePart,
    vehicle: DVehicle,
    brands: List<DVehicleBrand>
): List<String> {
    val labels = mutableListOf<String>()
    for (compatible in part.compatibleVehicles) {
        val brand = brands.find { it.id == compatible.brandId } ?: continue
        if (!brand.name.equals(vehicle.brand, ignoreCase = true)) continue
        val model = compatible.modelId?.let { id -> brand.models.find { it.id == id } }
        if (model != null && !model.name.equals(vehicle.model, ignoreCase = true)) continue
        val label = if (model != null) "${brand.name} ${model.name}" else brand.name
        if (label !in labels) labels += label
    }
    return labels
}
